import React, { useEffect, useState } from "react";
import { api, fmtDateTime } from "@/lib/apiClient";
import { Bell } from "@phosphor-icons/react";
import {
    DropdownMenu,
    DropdownMenuTrigger,
    DropdownMenuContent,
    DropdownMenuItem,
    DropdownMenuSeparator,
    DropdownMenuLabel,
} from "@/components/ui/dropdown-menu";
import { useNavigate } from "react-router-dom";

export default function NotificationsBell() {
    const [items, setItems] = useState([]);
    const navigate = useNavigate();

    const load = async () => {
        try {
            const { data } = await api.get("/notifications");
            setItems(Array.isArray(data) ? data : []);
        } catch (e) {
            setItems([]);
        }
    };

    useEffect(() => {
        load();
        const id = setInterval(load, 30000);
        return () => clearInterval(id);
    }, []);

    const unread = items.filter((n) => !n.read).length;

    const onItemClick = async (n) => {
        try {
            await api.post(`/notifications/${n.id}/read`);
        } catch (e) {
            /* best-effort */
        }
        if (n.link) navigate(n.link);
        load();
    };

    const onMarkAll = async () => {
        try {
            await api.post("/notifications/mark-all-read");
        } catch (e) {
            /* best-effort */
        }
        load();
    };

    return (
        <DropdownMenu>
            <DropdownMenuTrigger asChild>
                <button
                    data-testid="notifications-bell"
                    className="relative p-2 hover:bg-slate-100 text-slate-700"
                >
                    <Bell size={20} />
                    {unread > 0 && (
                        <span
                            data-testid="notifications-unread-count"
                            className="absolute top-0.5 right-0.5 min-w-[16px] h-[16px] px-1 bg-[var(--c-vermilion)] text-white text-[10px] font-mono font-bold flex items-center justify-center"
                        >
                            {unread > 9 ? "9+" : unread}
                        </span>
                    )}
                </button>
            </DropdownMenuTrigger>
            <DropdownMenuContent
                align="end"
                className="w-80 rounded-none border-slate-200"
                data-testid="notifications-dropdown"
            >
                <DropdownMenuLabel className="flex justify-between items-center">
                    <span className="overline">Notifications</span>
                    <button
                        onClick={onMarkAll}
                        className="text-xs text-[var(--c-accent)] hover:underline font-medium"
                        data-testid="mark-all-read-button"
                    >
                        Mark all read
                    </button>
                </DropdownMenuLabel>
                <DropdownMenuSeparator />
                {items.length === 0 ? (
                    <div className="px-3 py-6 text-sm text-slate-500 text-center">No notifications yet.</div>
                ) : (
                    items.slice(0, 8).map((n) => (
                        <DropdownMenuItem
                            key={n.id}
                            onClick={() => onItemClick(n)}
                            className={`flex flex-col items-start gap-1 px-3 py-2.5 cursor-pointer ${
                                n.read ? "opacity-70" : ""
                            }`}
                            data-testid={`notification-item-${n.id}`}
                        >
                            <div className="flex items-center gap-2 w-full">
                                {!n.read && (
                                    <span className="w-1.5 h-1.5 bg-[var(--c-accent)] inline-block" />
                                )}
                                <span className="text-sm font-medium flex-1">{n.title}</span>
                            </div>
                            <span className="text-xs text-slate-500 line-clamp-2">{n.body}</span>
                            <span className="text-[10px] font-mono text-slate-400">{fmtDateTime(n.created_at)}</span>
                        </DropdownMenuItem>
                    ))
                )}
            </DropdownMenuContent>
        </DropdownMenu>
    );
}
